import { experienceEvent } from '@/events/builders'
import type { EventTuple } from '@/events/types'
import { applyLiquidChanges, type LiquidChangeSet } from '@/play/liquid/changes'
import type { LiquidKind } from '@/play/liquid/kind'
import type { PageEventHandler } from '@/play/pageTypes'
import type { VoxelData, VoxelMap } from '@/voxelTypes'

/**
 * The durable side of the gravity tool: one event per frame of gravity (what the rigid bodies and
 * the flow moved), coalesced into one undo step per hold. Kept apart from the runner so the reducer
 * (which imports pages' events.ts) never imports the sims or the store.
 */

/** Rigid cells written (a moved voxel lands, keeping its colour and material) and cleared. */
export type VoxelChangeSet = { set: Array<[key: number, voxel: VoxelData]>; clear: number[] }

/** One frame of gravity: the rigid moves and the flow's change set (the one LiquidSim's). */
export type GravityChangeSet = { rigid: VoxelChangeSet; flow: LiquidChangeSet }

/**
 * Applies a frame in the order that keeps both sims' writes valid: flow cells leave, rigid cells
 * leave, rigid cells arrive (over anything), flow cells arrive (only into empty or flow cells).
 */
export function applyGravityChanges(base: VoxelMap, changes: GravityChangeSet, kind: LiquidKind | null): void {
  const flow = changes.flow ?? { set: [], clear: [] }
  if (kind && flow.clear.length > 0) applyLiquidChanges(base, { set: [], clear: flow.clear }, undefined, kind)
  for (const key of changes.rigid?.clear ?? []) base.delete(key)
  for (const [key, v] of changes.rigid?.set ?? []) base.set(key, v)
  if (kind && flow.set.length > 0) applyLiquidChanges(base, { set: flow.set, clear: [] }, undefined, kind)
}

/** A change set being folded in place, with each key's place in its `set` or `clear` array. */
class Fold<T> {
  readonly acc: { set: Array<[number, T]>; clear: number[] } = { set: [], clear: [] }
  private readonly setAt = new Map<number, number>()
  private readonly clearAt = new Map<number, number>()

  add(b: { set: Array<[number, T]>; clear: number[] } | undefined): void {
    if (!b) return
    for (const key of b.clear) {
      const si = this.setAt.get(key)
      if (si !== undefined) this.removeSet(si, key)
      if (!this.clearAt.has(key)) {
        this.clearAt.set(key, this.acc.clear.length)
        this.acc.clear.push(key)
      }
    }
    for (const [key, v] of b.set) {
      const ci = this.clearAt.get(key)
      if (ci !== undefined) this.removeClear(ci, key)
      const si = this.setAt.get(key)
      if (si !== undefined) this.acc.set[si] = [key, v]
      else {
        this.setAt.set(key, this.acc.set.length)
        this.acc.set.push([key, v])
      }
    }
  }

  private removeSet(i: number, key: number) {
    const arr = this.acc.set
    const last = arr.length - 1
    if (i !== last) { arr[i] = arr[last]; this.setAt.set(arr[i][0], i) }
    arr.pop()
    this.setAt.delete(key)
  }

  private removeClear(i: number, key: number) {
    const arr = this.acc.clear
    const last = arr.length - 1
    if (i !== last) { arr[i] = arr[last]; this.clearAt.set(arr[i], i) }
    arr.pop()
    this.clearAt.delete(key)
  }
}

/**
 * A page's durable gravity event (`experience.<page>.<verb>`) for a behaviour table's flow kind
 * (null when the table has no flow): the event builder, the coalescer a draft folds a hold with
 * (in place, O(cells) for the whole hold) and the reducer handler to export from `events.ts`.
 *
 * The rigid half folds per key (the later write wins). A cell that the rigid half sets and the
 * flow half later clears, or the other way round, is left to the apply order; the sims never claim
 * one cell in the same frame.
 */
export function gravityEvent(name: string, kind: LiquidKind | null) {
  const tag = name as `experience.${string}`
  let current: { payload: GravityChangeSet; rigid: Fold<VoxelData>; flow: Fold<number> } | null = null
  return {
    name,
    event: (changes: GravityChangeSet): EventTuple => experienceEvent(tag, changes),
    combine: (a: EventTuple, b: EventTuple): EventTuple | null => {
      if (a[0] !== name || b[0] !== name) return null
      const pa = a[1] as GravityChangeSet
      const pb = b[1] as GravityChangeSet
      if (current && current.payload === pa) {
        current.rigid.add(pb.rigid)
        current.flow.add(pb.flow)
        return a
      }
      const rigid = new Fold<VoxelData>()
      const flow = new Fold<number>()
      rigid.add(pa.rigid)
      flow.add(pa.flow)
      rigid.add(pb.rigid)
      flow.add(pb.flow)
      const payload: GravityChangeSet = { rigid: rigid.acc, flow: flow.acc }
      current = { payload, rigid, flow }
      return experienceEvent(tag, payload)
    },
    handlers: {
      [name]: (ctx, event) => {
        applyGravityChanges(ctx.base, event[1] as GravityChangeSet, kind)
      },
    } as Record<string, PageEventHandler>,
  }
}

export type GravityEvent = ReturnType<typeof gravityEvent>
